import React, { useEffect, useRef } from 'react';
import Cell from '../Cell';
import { usePuzzleState } from '../../context/PuzzleStateContext';
import styles from './GridBoard.module.css';

const GridBoard = () => {
  const { 
    puzzleState, 
    optimisticGrid, 
    selectedCell, 
    setSelectedCell,
    makeMove,
    setNotes,
    deleteAllNotes,
    eraseCell,
    isLoading,
    selectCell,
    isNoteMode
  } = usePuzzleState();

  const gridRef = useRef(null);

  // Use optimistic grid if available, otherwise fall back to puzzle state grid
  const grid = optimisticGrid || puzzleState?.grid;
  const size = puzzleState?.puzzle?.size;

  // Grid sizing is now handled by the useResponsiveLayout hook in GameContainer

  const handleCellClick = (x, y) => {
    // Check if we should send the selection to the backend (create mode stage 2+)
    if (grid?.send_select) {
      // Update local selected cell state for actions like reveal, delete, etc.
      setSelectedCell({ x, y });
      // Also send selection to backend for cage selection logic
      selectCell(x, y);
    } else {
      // Normal behavior - just set the selected cell locally
      setSelectedCell({ x, y });
    }
  };

  // Handle keyboard navigation and input
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only process keyboard events if we have a selected cell and not in loading state
      if (!selectedCell || isLoading || !grid?.allowed_values) return;
      
      // Handle number/value keys for direct input
      if (/^[0-9]$/.test(e.key) || grid.allowed_values.includes(e.key)) {
        // Check if the pressed key is in the allowed values
        if (grid.allowed_values.includes(e.key)) {
          handleValueInput(e.key);
        }
      }
      // Handle arrow keys for navigation
      else if (e.key.startsWith('Arrow')) {
        e.preventDefault(); // Prevent scrolling
        navigateWithArrowKeys(e.key);
      }
      // Handle Backspace/Delete for erasing
      else if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        handleEraseInput();
      }
    };

    // Function to navigate with arrow keys
    const navigateWithArrowKeys = (arrowKey) => {
      if (!selectedCell || !grid?.cells) return;
      
      const { x, y } = selectedCell;
      let newX = x;
      let newY = y;
      
      switch (arrowKey) {
        case 'ArrowUp':
          newY = Math.max(0, y - 1);
          break;
        case 'ArrowDown':
          newY = Math.min(size - 1, y + 1);
          break;
        case 'ArrowLeft':
          newX = Math.max(0, x - 1);
          break;
        case 'ArrowRight':
          newX = Math.min(size - 1, x + 1);
          break;
        default:
          return;
      }
      
      // Only update if the position actually changed
      if (newX !== x || newY !== y) {
        // Ensure the new position has a valid cell
        const newCell = grid.cells.find(
          cell => cell.x === newX && cell.y === newY
        );
        
        if (newCell) {
          setSelectedCell({ x: newX, y: newY });
        }
      }
    };

    // Handle value input
    const handleValueInput = async (value) => {
      if (!selectedCell) return;
      
      const { x, y } = selectedCell;
      
      // Check if we're in note mode
      if (isNoteMode) {
        // In note mode, add/remove notes
        await setNotes(x, y, value.toString());
      } else {
        // In value mode, set the cell value
        await makeMove(x, y, value.toString());
      }
    };

    // Handle erase input (Backspace/Delete key)
    const handleEraseInput = async () => {
      if (!selectedCell) return;
      
      const { x, y } = selectedCell;
      
      // Check if we're in note mode
      if (isNoteMode) {
        // In note mode, delete all notes
        await deleteAllNotes(x, y);
      } else {
        // In value mode, erase the cell value
        await eraseCell(x, y);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedCell, isLoading, grid?.allowed_values, grid?.cells, size, makeMove, setNotes, deleteAllNotes, eraseCell, setSelectedCell, isNoteMode]);

  // Focus the grid when a cell is selected
  useEffect(() => {
    if (selectedCell && gridRef.current) {
      gridRef.current.focus();
    }
  }, [selectedCell]);

  // Early return AFTER all hooks are called
  if (!grid || !size) {
    return <div className={styles.loading}>Loading grid...</div>;
  }

  const renderGrid = () => {
    const rows = [];
    
    // Group cells by row
    for (let y = 0; y < size; y++) {
      const rowCells = grid.cells.filter(cell => cell.y === y);
      rowCells.sort((a, b) => a.x - b.x);
      
      rows.push(
        <div key={`row-${y}`} className={styles.gridRow}>
          {rowCells.map(cell => {
            // Get the current value for this cell from state.current_values (or fallback to cell.value)
            const currentValue = (puzzleState?.state?.current_values && puzzleState.state.current_values[cell.y]?.[cell.x] !== undefined)
              ? puzzleState.state.current_values[cell.y][cell.x]
              : cell.value;
            
            // Check if this cell is selected - either locally or from backend selected_cells
            let isSelected = false;
            if (grid?.send_select && grid?.selected_cells) {
              // When send_select is true, use the backend's selected_cells array
              isSelected = grid.selected_cells.some(selectedCell => 
                selectedCell.x === cell.x && selectedCell.y === cell.y
              );
            } else {
              // Normal behavior - use local selectedCell
              isSelected = selectedCell?.x === cell.x && selectedCell?.y === cell.y;
            }
            
            return (
              <Cell
                key={`cell-${cell.x}-${cell.y}`}
                cell={{...cell, value: currentValue}}
                grid={grid}
                size={size}
                isSelected={isSelected}
                onCellClick={handleCellClick}
              />
            );
          })}
        </div>
      );
    }
    
    return rows;
  };

  return (
    <div 
      className={styles.grid} 
      ref={gridRef}
      tabIndex={0}
      style={{ '--grid-size': size }}
      data-size={size}
    >
      {renderGrid()}
    </div>
  );
};

export default GridBoard;
